import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, EyeIcon, EyeOffIcon, LockIcon } from '../components/Icons';
import * as authService from '../services/api/auth';
import './ResetPassword.css';

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: location.state?.email || '',
    otp: location.state?.otp || '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    if (!/^\d{6}$/.test(formData.otp.trim())) {
      setError('OTP must be exactly 6 digits.');
      return;
    }
    if (formData.newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const result = await authService.resetPassword({
        email: formData.email.trim(),
        otp: formData.otp.trim(),
        new_password: formData.newPassword,
        confirm_password: formData.confirmPassword,
      });

      if (result.success) {
        setSuccess('Password reset successful. Redirecting to login...');
        setTimeout(() => navigate('/login'), 1500);
      } else {
        setError(result.message || 'Failed to reset password.');
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reset-password-screen">
      <div className="reset-password-container">
        <div className="reset-password-card">
          <div className="reset-password-header">
            <button className="reset-password-back" onClick={() => navigate('/verify-otp', { state: { email: formData.email } })} aria-label="Back">
              <ArrowLeftIcon size={20} color="currentColor" />
            </button>
            <h1 className="reset-password-header-title">Reset Password</h1>
            <div className="reset-password-spacer" />
          </div>

          <div className="reset-password-content">
            <div className="reset-password-icon" aria-hidden="true">
              <LockIcon size={52} color="#2563eb" />
            </div>

            <h2 className="reset-password-title">Create New Password</h2>
            <p className="reset-password-subtitle">
              Choose a strong password that you do not use on other accounts.
            </p>

            <form onSubmit={handleSubmit} className="reset-password-form">
              {error && <div className="reset-password-error">{error}</div>}
              {success && <div className="reset-password-success">{success}</div>}

              <div className="reset-password-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="reset-password-input"
                  value={formData.email}
                  onChange={handleChange}
                  autoCapitalize="none"
                  required
                />
              </div>

              <div className="reset-password-group">
                <label htmlFor="otp">OTP</label>
                <input
                  type="text"
                  id="otp"
                  name="otp"
                  className="reset-password-input reset-password-code"
                  value={formData.otp}
                  onChange={(e) => setFormData((prev) => ({ ...prev, otp: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                  inputMode="numeric"
                  placeholder="Enter 6-digit OTP"
                  required
                />
              </div>

              <div className="reset-password-group">
                <label htmlFor="newPassword">New Password</label>
                <div className="reset-password-password-wrapper">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    id="newPassword"
                    name="newPassword"
                    className="reset-password-input"
                    value={formData.newPassword}
                    onChange={handleChange}
                    placeholder="Minimum 8 characters"
                    required
                  />
                  <button
                    type="button"
                    className="reset-password-toggle"
                    onClick={() => setShowNewPassword((v) => !v)}
                    aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                  >
                    {showNewPassword ? <EyeOffIcon size={18} color="currentColor" /> : <EyeIcon size={18} color="currentColor" />}
                  </button>
                </div>
              </div>

              <div className="reset-password-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <div className="reset-password-password-wrapper">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    name="confirmPassword"
                    className="reset-password-input"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Re-enter new password"
                    required
                  />
                  <button
                    type="button"
                    className="reset-password-toggle"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? <EyeOffIcon size={18} color="currentColor" /> : <EyeIcon size={18} color="currentColor" />}
                  </button>
                </div>
              </div>

              <button type="submit" className="btn-primary reset-password-submit" disabled={loading}>
                {loading ? 'Resetting Password...' : 'Reset Password'}
              </button>
            </form>

            <div className="reset-password-login-link">
              <Link to="/login" className="reset-password-back-link">
                <ArrowLeftIcon size={16} color="currentColor" />
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
